import React from "react";
import { Text, View } from "react-native";

interface ErrorBoundaryState {
	hasError: boolean;
	error?: Error;
}

interface ErrorBoundaryProps {
	children: React.ReactNode;
	fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>;
}

/**
 * Error Boundary for catching React errors in the component tree
 * Provides graceful error handling and recovery
 */
export class ErrorBoundary extends React.Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		console.error("Error Boundary caught an error:", error, errorInfo);
	}

	resetError = () => {
		this.setState({ hasError: false, error: undefined });
	};

	render() {
		if (this.state.hasError) {
			if (this.props.fallback) {
				const FallbackComponent = this.props.fallback;
				return (
					<FallbackComponent
						error={this.state.error}
						resetError={this.resetError}
					/>
				);
			}

			return (
				<View
					style={{
						flex: 1,
						justifyContent: "center",
						alignItems: "center",
						padding: 20,
					}}
				>
					<Text
						style={{
							fontSize: 18,
							fontWeight: "bold",
							marginBottom: 10,
							color: "#ef4444",
						}}
					>
						Something went wrong
					</Text>
					<Text
						style={{ textAlign: "center", marginBottom: 20, color: "#6b7280" }}
					>
						{this.state.error?.message || "An unexpected error occurred"}
					</Text>
					<button
						onClick={this.resetError}
						style={{
							backgroundColor: "#3b82f6",
							color: "white",
							padding: "10px 20px",
							borderRadius: 5,
							border: "none",
							cursor: "pointer",
						}}
					>
						Try Again
					</button>
				</View>
			);
		}

		return this.props.children;
	}
}
